import type { PlanDefinition } from "@medplum/fhirtypes";
import {
  programDefinitionAvailability,
  programDefinitionDays,
  programDefinitionDescription,
  programDefinitionMintedAt,
  programDefinitionName,
  programDefinitionUrl,
  programDefinitionVersion,
} from "../fhir/program-definition-storage";
import type { ProgramDefinitionDto } from "./program-definition";
import { projectProtocolDefinitionRow } from "./protocol-definition-projection";

export type ProgramDefinitionProjectionRow = PlanDefinition & {
  relatedPlanDefinitions: unknown;
  optimisticProgramDefinition?: ProgramDefinitionDto;
};

export function projectProgramDefinitionRow(
  resource: PlanDefinition,
  protocols: readonly PlanDefinition[] = [],
): ProgramDefinitionDto {
  const description = programDefinitionDescription(resource);
  const protocolsByUrl = new Map(protocols.flatMap((protocol) => (protocol.url ? [[protocol.url, protocol]] : [])));
  return {
    definitionUrl: programDefinitionUrl(resource),
    version: programDefinitionVersion(resource),
    mintedAt: programDefinitionMintedAt(resource),
    name: programDefinitionName(resource),
    ...(description === undefined ? {} : { description }),
    availability: programDefinitionAvailability(resource),
    days: programDefinitionDays(resource).map((day, dayIndex) => ({
      dayId: day.id,
      dayNumber: dayIndex + 1,
      sessions: day.sessions.map((session) => {
        const row = protocolsByUrl.get(session.protocolUrl);
        // A session names a LINEAGE, so its current revision may not be loaded
        // (or may have been retired). The program still reads; the name does not.
        const protocol = row ? projectProtocolDefinitionRow(row) : undefined;
        return {
          sessionId: session.id,
          timeWindow: session.timeWindow,
          protocolDefinitionUrl: session.protocolUrl,
          protocolName: protocol?.name ?? "Unavailable protocol",
          ...(protocol ? { totalDurationMinutes: protocol.totalDurationMinutes } : {}),
        };
      }),
    })),
  };
}

export function projectIncludedProgramDefinition(row: ProgramDefinitionProjectionRow): ProgramDefinitionDto {
  if (row.optimisticProgramDefinition) return row.optimisticProgramDefinition;
  const { relatedPlanDefinitions, ...definition } = row;
  return projectProgramDefinitionRow(
    definition,
    Array.isArray(relatedPlanDefinitions) ? (relatedPlanDefinitions as PlanDefinition[]) : [],
  );
}
